import React, { useState, useEffect } from "react";
import { makeStyles, Divider } from "@material-ui/core";
import { useCaseListContext } from "../../context/CaseListContext";
import RightDrawer from "../../components/shared/RightDrawer";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getCaseType, getEvents } from "../../components/shared/helpers/helper";
import { useStore } from "../../context/StoreContext";
import Select from "react-select";
import { Typography } from "@mui/material";
import { tokens } from "../../theme/tokens";

import {
  DeleteButton,
  StyledTabButton,
} from "../shared/helpers/GlobalStylesv5";

const useStyles = makeStyles((theme) => ({
  drawerContent: {
    display: "flex",
    flexDirection: "column",
    height: "100%", // Fill the contentWrapper
  },
  scrollableContent: {
    flex: 1,
    overflowY: "auto",
    padding: "20px", // Add the padding back here
  },
  footer: {
    padding: "16px 20px",
    borderTop: `1px solid ${tokens.color.border.default}`,
    backgroundColor: "white",
    display: "flex",
    gap: "10px",
    zIndex: 10, // Ensure it sits above scrolling content
  },

  filterSection: {
    display: "flex",
    gap: "30px",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: "0.875em",
    fontWeight: 600,
    color: "#333",
    letterSpacing: "0.5px",
    minWidth: "120px",
    alignSelf: "flex-start",
  },
  selectContainer: {
    flex: 1,
    maxWidth: "14vw",
  },
  dateSection: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
    alignItems: "stretch",
    maxWidth: "14vw",
  },
  datePickerWrapper: {
    minHeight: "35px",
    flex: 1,
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: tokens.color.surface.sunken,
      borderRadius: "10px",
      fontSize: "0.85rem",

      "& fieldset": {
        border: `1px solid ${tokens.color.border.default} !important`,
      },
      "&:hover fieldset": {
        borderColor: `${tokens.color.brand.primary} !important`,
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      color: "#666",
    },
    // This helps prevent the label from overlapping with Select dropdowns
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
      backgroundColor: "#fff", // Gives label a clean background when floating
      padding: "0 4px",
    },
  },
  divider: {
    margin: "10px 0",
  },
  appliedFiltersSection: {
    marginBottom: "20px",
    padding: "12px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  appliedFiltersTitle: {
    fontSize: "0.9em",
    fontWeight: 500,
    color: "#555",
    marginBottom: "10px",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  chip: {
    border: `1px solid ${tokens.color.brand.primary}`,
    backgroundColor: "white",
  },

  applyButton: {
    flex: 1,
    // backgroundColor: tokens.color.brand.primaryAction,
    color: "white",
    textTransform: "capitalize",
    padding: "10px",
    fontWeight: 500,
    "&:hover": {
      // backgroundColor: "#007a99",
    },
  },
  clearButton: {
    flex: 1,
    textTransform: "capitalize",
    border: `1px solid ${tokens.color.border.default}`,
    padding: "10px",
    fontWeight: 500,
  },
}));

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const { users, clients } = useStore();

  const { pendingFilters, setPendingFilters, setFilters, setPage } =
    useCaseListContext();

  // Local state for each filter type
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [eventFilter, setEventFilter] = useState<string>("");
  const [clientFilter, setClientFilter] = useState<string>("");
  const [userFilter, setUserFilter] = useState<string>("");
  const [caseTypeFilter, setCaseTypeFilter] = useState<string>("");

  // Date filters
  const [followUpFrom, setFollowUpFrom] = useState<dayjs.Dayjs | null>(null);
  const [followUpTo, setFollowUpTo] = useState<dayjs.Dayjs | null>(null);
  const [invoiceFrom, setInvoiceFrom] = useState<dayjs.Dayjs | null>(null);
  const [invoiceTo, setInvoiceTo] = useState<dayjs.Dayjs | null>(null);
  const [dueFrom, setDueFrom] = useState<dayjs.Dayjs | null>(null);
  const [dueTo, setDueTo] = useState<dayjs.Dayjs | null>(null);
  const [createdFrom, setCreatedFrom] = useState<dayjs.Dayjs | null>(null);
  const [createdTo, setCreatedTo] = useState<dayjs.Dayjs | null>(null);

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const eventOptions = [...getEvents(), "Case Created"].map((event) => ({
    value: event,
    label: event,
  }));

  const caseTypeOptions = getCaseType().map((type) => ({
    value: type,
    label: type,
  }));

  const clientOptions =
    clients?.map((client) => ({
      value: client,
      label: client,
    })) || [];

  const userOptions =
    users?.map((user) => ({
      value: user,
      label: user,
    })) || [];

  // Initialize filters from pendingFilters when drawer opens
  useEffect(() => {
    if (open) {
      // Parse existing pending filters
      pendingFilters.forEach((filter) => {
        switch (filter.key) {
          case "status":
            setStatusFilter(filter.value ?? "");
            break;
          case "event":
            setEventFilter(filter.value ?? "");
            break;
          case "client":
            setClientFilter(filter.value ?? "");
            break;
          case "user":
            setUserFilter(filter.value ?? "");
            break;
          case "case_type":
            setCaseTypeFilter(filter.value ?? "");
            break;
          case "follow_up":
            if (filter.value) {
              const [ffrom, fto] = filter.value.split("-");
              setFollowUpFrom(ffrom ? dayjs(ffrom) : null);
              setFollowUpTo(fto ? dayjs(fto) : null);
            }
            break;
          case "invoice_date":
            if (filter.value) {
              const [ifrom, ito] = filter.value.split("-");
              setInvoiceFrom(ifrom ? dayjs(ifrom) : null);
              setInvoiceTo(ito ? dayjs(ito) : null);
            }
            break;
          case "due_date":
            if (filter.value) {
              const [dfrom, dto] = filter.value.split("-");
              setDueFrom(dfrom ? dayjs(dfrom) : null);
              setDueTo(dto ? dayjs(dto) : null);
            }
            break;
          case "created_at":
            if (filter.value) {
              const [cfrom, cto] = filter.value.split("-");
              setCreatedFrom(cfrom ? dayjs(cfrom) : null);
              setCreatedTo(cto ? dayjs(cto) : null);
            }
            break;
        }
      });
    }
  }, [open, pendingFilters]);

  // Build filters array from current selections
  const buildFilters = () => {
    const filters: any[] = [];

    if (statusFilter) {
      filters.push({ type: "select", key: "status", value: statusFilter });
    }
    if (eventFilter) {
      filters.push({ type: "select", key: "event", value: eventFilter });
    }
    if (clientFilter) {
      filters.push({ type: "select", key: "client", value: clientFilter });
    }
    if (userFilter) {
      filters.push({ type: "select", key: "user", value: userFilter });
    }
    if (caseTypeFilter) {
      filters.push({ type: "select", key: "case_type", value: caseTypeFilter });
    }

    // Date filters
    if (followUpFrom && followUpTo) {
      filters.push({
        type: "date",
        key: "follow_up",
        value: `${followUpFrom.format("YYYY/MM/DD")}-${followUpTo.format(
          "YYYY/MM/DD",
        )}`,
      });
    }
    if (invoiceFrom && invoiceTo) {
      filters.push({
        type: "date",
        key: "invoice_date",
        value: `${invoiceFrom.format("YYYY/MM/DD")}-${invoiceTo.format(
          "YYYY/MM/DD",
        )}`,
      });
    }
    if (dueFrom && dueTo) {
      filters.push({
        type: "date",
        key: "due_date",
        value: `${dueFrom.format("YYYY/MM/DD")}-${dueTo.format("YYYY/MM/DD")}`,
      });
    }
    if (createdFrom && createdTo) {
      filters.push({
        type: "date",
        key: "created_at",
        value: `${createdFrom.format("YYYY/MM/DD")}-${createdTo.format(
          "YYYY/MM/DD",
        )}`,
      });
    }

    return filters;
  };

  const handleApplyFilters = () => {
    const finalFilters = buildFilters();
    setPendingFilters(finalFilters);
    setFilters(finalFilters);
    setPage(1);
    onClose();
  };

  const handleClearAll = () => {
    setStatusFilter("");
    setEventFilter("");
    setClientFilter("");
    setUserFilter("");
    setCaseTypeFilter("");
    setFollowUpFrom(null);
    setFollowUpTo(null);
    setInvoiceFrom(null);
    setInvoiceTo(null);
    setDueFrom(null);
    setDueTo(null);
    setCreatedFrom(null);
    setCreatedTo(null);
    setPendingFilters([]);
    setFilters([]);
    setPage(1);
  };

  const getActiveFiltersCount = () => {
    return buildFilters().length;
  };

  return (
    <RightDrawer open={open} onClose={onClose} title="Filters">
      <div className={classes.drawerContent}>
        <div className={classes.scrollableContent}>
          {/* Status Filter */}
          <div className={classes.filterSection}>
            <Typography className={classes.sectionTitle}>Status</Typography>
            <div className={classes.selectContainer}>
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                value={
                  statusFilter
                    ? {
                        value: statusFilter,
                        label:
                          statusFilter.charAt(0).toUpperCase() +
                          statusFilter.slice(1),
                      }
                    : null
                }
                onChange={(option) => setStatusFilter(option?.value || "")}
                options={statusOptions}
                isClearable
                placeholder="Select status"
              />
            </div>
          </div>

          <Divider className={classes.divider} />

          {/* Event Filter */}
          <div className={classes.filterSection}>
            <Typography className={classes.sectionTitle}>Event</Typography>
            <div className={classes.selectContainer}>
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                value={
                  eventFilter
                    ? { value: eventFilter, label: eventFilter }
                    : null
                }
                onChange={(option) => setEventFilter(option?.value || "")}
                options={eventOptions}
                isClearable
                placeholder="Select event"
              />
            </div>
          </div>

          <Divider className={classes.divider} />

          {/* Case Type Filter */}
          <div className={classes.filterSection}>
            <Typography className={classes.sectionTitle}>Case Type</Typography>
            <div className={classes.selectContainer}>
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                value={
                  caseTypeFilter
                    ? { value: caseTypeFilter, label: caseTypeFilter }
                    : null
                }
                onChange={(option) => setCaseTypeFilter(option?.value || "")}
                options={caseTypeOptions}
                isClearable
                placeholder="Select case type"
              />
            </div>
          </div>

          <Divider className={classes.divider} />

          {/* Client Filter */}
          <div className={classes.filterSection}>
            <Typography className={classes.sectionTitle}>Client</Typography>
            <div className={classes.selectContainer}>
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                value={
                  clientFilter
                    ? { value: clientFilter, label: clientFilter }
                    : null
                }
                onChange={(option) => setClientFilter(option?.value || "")}
                options={clientOptions}
                isClearable
                placeholder="Select client"
              />
            </div>
          </div>

          <Divider className={classes.divider} />

          {/* User Filter */}
          <div className={classes.filterSection}>
            <Typography className={classes.sectionTitle}>User</Typography>
            <div className={classes.selectContainer}>
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                value={
                  userFilter ? { value: userFilter, label: userFilter } : null
                }
                onChange={(option) => setUserFilter(option?.value || "")}
                options={userOptions}
                isClearable
                placeholder="Select user"
              />
            </div>
          </div>

          <Divider className={classes.divider} />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* Follow Up Date */}
            <div className={classes.filterSection}>
              <Typography className={classes.sectionTitle}>
                Follow Up Date
              </Typography>
              <div className={classes.dateSection}>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="From"
                    format="YYYY/MM/DD"
                    value={followUpFrom}
                    onChange={(newValue) => setFollowUpFrom(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="To"
                    format="YYYY/MM/DD"
                    value={followUpTo}
                    onChange={(newValue) => setFollowUpTo(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
              </div>
            </div>

            <Divider className={classes.divider} />

            {/* Invoice Date */}
            <div className={classes.filterSection}>
              <Typography className={classes.sectionTitle}>
                Invoice Date
              </Typography>
              <div className={classes.dateSection}>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="From"
                    format="YYYY/MM/DD"
                    value={invoiceFrom}
                    onChange={(newValue) => setInvoiceFrom(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="To"
                    format="YYYY/MM/DD"
                    value={invoiceTo}
                    onChange={(newValue) => setInvoiceTo(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
              </div>
            </div>

            <Divider className={classes.divider} />

            {/* Due Date */}
            <div className={classes.filterSection}>
              <Typography className={classes.sectionTitle}>Due Date</Typography>
              <div className={classes.dateSection}>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="From"
                    format="YYYY/MM/DD"
                    value={dueFrom}
                    onChange={(newValue) => setDueFrom(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="To"
                    format="YYYY/MM/DD"
                    value={dueTo}
                    onChange={(newValue) => setDueTo(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
              </div>
            </div>

            <Divider className={classes.divider} />

            {/* Created At Date */}
            <div className={classes.filterSection}>
              <Typography className={classes.sectionTitle}>
                Created At
              </Typography>
              <div className={classes.dateSection}>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="From"
                    format="YYYY/MM/DD"
                    value={createdFrom}
                    onChange={(newValue) => setCreatedFrom(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    label="To"
                    format="YYYY/MM/DD"
                    value={createdTo}
                    onChange={(newValue) => setCreatedTo(newValue)}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </div>
              </div>
            </div>
          </LocalizationProvider>
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className={classes.footer}>
          <DeleteButton
            onClick={handleClearAll}
            className={classes.clearButton}
          >
            Clear All
          </DeleteButton>
          <StyledTabButton
            onClick={handleApplyFilters}
            className={classes.applyButton}
          >
            Apply Filters
            {getActiveFiltersCount() > 0 && ` (${getActiveFiltersCount()})`}
          </StyledTabButton>
        </div>
      </div>
    </RightDrawer>
  );
};

export default FilterDrawer;
